import {Box, Button, IconButton, Modal, Paper, Typography} from '@mui/material';
import {DataGrid, GridColDef, GridRenderCellParams} from '@mui/x-data-grid';
import React, {useCallback} from 'react';
import {Surface} from '../../interfaces';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {deleteSurface, updateSurface} from '../../api/adminAPI';
import {Control, FieldValues, useForm} from 'react-hook-form';
import FormTextField from '../shared/FormTextField';
import styles from '../../styles';
import {useTranslation} from 'react-i18next';

interface Props {
  surfaces: Surface[];
  height?: number;
  onUpdate: () => void;
}

type SurfaceEditForm = {
  id: number;
  name: string;
  livebarnStreamName: string;
};

const SurfacesTable: React.FC<Props> = ({surfaces, height = 400, onUpdate}) => {
  const {t} = useTranslation();
  const [editingSurface, setEditingSurface] = React.useState<Surface | null>(
    null,
  );
  const [pending, setPending] = React.useState(false);

  const {handleSubmit, reset, control, setValue} = useForm<SurfaceEditForm>();

  const handleDelete = useCallback(
    async (id: number) => {
      if (window.confirm(t('admin.confirmDeleteSurface') as string)) {
        await deleteSurface({id});
        onUpdate();
      }
    },
    [onUpdate, t],
  );

  const handleEditClick = useCallback(
    (surface: Surface) => {
      setValue('id', surface.id || 0);
      setValue('name', surface.name || '');
      setValue('livebarnStreamName', surface.livebarnStreamName || '');
      setEditingSurface(surface);
    },
    [setValue],
  );

  const handleEditClose = useCallback(() => {
    reset();
    setEditingSurface(null);
    setPending(false);
  }, [reset]);

  const handleEditSubmit = useCallback(
    async (data: SurfaceEditForm) => {
      setPending(true);
      await updateSurface({
        id: data.id,
        name: data.name,
        livebarnStreamName: data.livebarnStreamName || undefined,
      });
      onUpdate();
      handleEditClose();
    },
    [handleEditClose, onUpdate],
  );

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
      filterable: true,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      filterable: true,
    },
    {
      field: 'livebarnStreamName',
      headerName: 'LiveBarn Stream',
      width: 200,
      filterable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleEditClick(params.row);
            }}>
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleDelete(params.row.id);
            }}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Paper sx={{height}}>
        <DataGrid rows={surfaces ?? []} columns={columns} />
      </Paper>
      <Modal
        open={!!editingSurface}
        onClose={handleEditClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('admin.updateSurface')}
          </Typography>
          <form onSubmit={handleSubmit(handleEditSubmit)} className="form">
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="name"
              label={t('general.name') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="livebarnStreamName"
              label={t('admin.livebarnStreamName') as string}
            />
            <Button disabled={pending} type="submit">
              {t('general.submit')}
            </Button>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(SurfacesTable);
